import {
  type Credentials,
  getOrCreateConversation,
  getProfile,
  setName,
  useGetOrCreateConversation,
} from "@alice-and-bot/core";
import { Chat } from "@alice-and-bot/core/components";
import { useEffect, useRef, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { builderBotPublicSignKey } from "../../constants.ts";
import type { InstaQLEntity } from "@instantdb/react";
import schema from "../../instant.schema.ts";
import { Spinner, themeColors } from "./components.tsx";

type Bot = InstaQLEntity<typeof schema, "bots">;
import { useQuery } from "./db.ts";
import { useActiveUser } from "./loginHooks.ts";
import { usePageMeta } from "./pageMeta.ts";
import { getActiveTheme } from "./utils.ts";

const spinner = (
  <div className="flex items-center justify-center flex-1">
    <Spinner className="h-12 w-12" />
  </div>
);

const emptyMessage =
  "🤖 Ready to automate your business or daily tasks? Tell me what you'd like to automate and I'll help you set it up.";

const builderChatTitle = "prompt2bot builder";

const chatColorsForTheme = (theme: "light" | "dark") => {
  const colors = themeColors[theme];
  return {
    background: colors.surface,
    text: colors.text,
    primary: colors.accent,
    hideTitle: true,
    hideOwnAvatar: true,
    hideOtherBubble: true,
    hideNames: true,
    inputBackground: colors.surfaceRaised,
    inputMaxWidth: "700px",
    chatMaxWidth: "900px",
    scrollbarColor: theme === "dark"
      ? "#4b5563 transparent"
      : "#d1d5db transparent",
  };
};

const lightChatColors = chatColorsForTheme("light");
const darkChatColors = chatColorsForTheme("dark");

const BuilderChatInner = (
  { credentials, userId }: { credentials: Credentials; userId: string },
) => {
  const theme = getActiveTheme();
  const isExplicitBuilderPath = typeof location !== "undefined" &&
    location.pathname === "/builder-chat";

  const builderConversationId = useGetOrCreateConversation({
    credentials,
    participants: [builderBotPublicSignKey],
    initialMessage: undefined,
    title: builderChatTitle,
  });

  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(isExplicitBuilderPath ? builderConversationId : null);

  useEffect(() => {
    if (
      isExplicitBuilderPath && builderConversationId && !activeConversationId
    ) {
      setActiveConversationId(builderConversationId);
    }
  }, [isExplicitBuilderPath, builderConversationId, activeConversationId]);

  const { data: userData } = useQuery({
    $users: {
      $: { where: { id: userId } },
      bots: {},
    },
  });

  const initialBotsRecorded = useRef(false);
  const knownBotIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const bots = userData?.$users?.[0]?.bots;
    if (!bots) return;
    bots.forEach((b: Bot) => {
      if (!b?.aliceAndBotCredentials?.publicSignKey || !b?.name) return;
      getProfile(b.aliceAndBotCredentials.publicSignKey).then((res) => {
        if (res?.name && res.name !== b.name) {
          setName({
            credentials: b.aliceAndBotCredentials,
            name: b.name,
          }).catch((err) =>
            console.warn("Failed to auto-heal alice-and-bot name", err)
          );
        }
      }).catch(() => {});
    });
  }, [userData]);

  useEffect(() => {
    const bots = userData?.$users?.[0]?.bots;
    if (!bots) return;
    if (!initialBotsRecorded.current) {
      initialBotsRecorded.current = true;
      knownBotIds.current = new Set(bots.map((b: Bot) => b.id));
      return;
    }
    const newlyCreatedBot = bots.find((b: Bot) =>
      !knownBotIds.current.has(b.id)
    );
    if (newlyCreatedBot) {
      knownBotIds.current.add(newlyCreatedBot.id);
      const botPubKey = newlyCreatedBot.aliceAndBotCredentials?.publicSignKey;
      if (botPubKey) {
        toast.success(
          `🎉 "${newlyCreatedBot.name}" is ready! Switching to your new bot...`,
          { duration: 4000, icon: "🤖" },
        );
        const timer = setTimeout(() => {
          const sub = { unsub: undefined as (() => void) | undefined };
          sub.unsub = getOrCreateConversation(
            credentials,
            [botPubKey],
            (convId) => {
              if (convId) {
                setActiveConversationId(convId);
                queueMicrotask(() => sub.unsub?.());
              }
            },
            newlyCreatedBot.name,
          );
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [userData, credentials]);

  if (
    isExplicitBuilderPath && !activeConversationId && !builderConversationId
  ) {
    return spinner;
  }

  const currentId = activeConversationId ??
    (isExplicitBuilderPath ? builderConversationId : undefined);

  return (
    <Chat
      conversationId={currentId ?? ""}
      credentials={credentials}
      customColors={theme === "dark" ? darkChatColors : lightChatColors}
      darkModeOverride={theme === "dark"}
      emptyMessage={emptyMessage}
      enableChatSwitching
      onConversationChange={(e: unknown) => {
        const id = typeof e === "string"
          ? e
          : (e as { detail?: { conversationId?: string } })?.detail
            ?.conversationId;
        if (id) setActiveConversationId(id);
      }}
    />
  );
};

export const BuilderChat = () => {
  usePageMeta({
    title: "Chat | prompt2bot",
    description: "Chat with your AI agents and the prompt2bot builder.",
  });
  const user = useActiveUser();
  if (!user?.aliceAndBotCredentials || !user.id) return spinner;
  return (
    <BuilderChatInner
      credentials={user.aliceAndBotCredentials}
      userId={user.id}
    />
  );
};
